import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "~/components/ui/dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

interface TableResult {
  table: string;
  rlsEnabled: boolean;
  policyCount: number;
  matrix: Record<string, Record<string, { allowed: boolean; rowCount?: number; error?: string }>>;
}

interface BatchTestResponse {
  tables: TableResult[];
  summary: {
    totalTables: number;
    tablesWithRls: number;
    tablesWithoutRls: number;
    potentialIssues: number;
  };
  operations: string[];
  personas: string[];
}

export function RlsBatchTest({
  open,
  onOpenChange,
  schema,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BatchTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function fetchBatchTest() {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await fetch("/api/database/rls-test-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schema }),
      });
      const result = await res.json();
      if (!res.ok) {
        setError(result.error || "Failed to run batch test");
      } else {
        setData(result);
      }
    } catch (err: any) {
      setError(err.message || "Network error");
      toast.error("Failed to run RLS batch test");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) fetchBatchTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schema]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            RLS Batch Test
          </DialogTitle>
          <DialogDescription>
            Test RLS enforcement across all tables in the{" "}
            <span className="font-mono text-foreground">{schema}</span> schema.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Testing RLS policies across all tables...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <XCircle className="h-6 w-6 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchBatchTest}>
                <RefreshCw className="h-3 w-3 mr-1.5" />
                Retry
              </Button>
            </div>
          ) : data ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="secondary" className="text-xs">
                  {data.summary.totalTables} table{data.summary.totalTables !== 1 ? "s" : ""}
                </Badge>
                <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-xs">
                  {data.summary.tablesWithRls} with RLS
                </Badge>
                {data.summary.tablesWithoutRls > 0 && (
                  <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20 text-xs">
                    {data.summary.tablesWithoutRls} without RLS
                  </Badge>
                )}
                {data.summary.potentialIssues > 0 && (
                  <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {data.summary.potentialIssues} potential issue{data.summary.potentialIssues !== 1 ? "s" : ""}
                  </Badge>
                )}
              </div>

              {data.tables.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">No tables found in this schema.</p>
                </div>
              ) : (
                <div className="rounded border border-border overflow-auto">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="sticky top-0 bg-background z-10">
                      <tr>
                        <th className="text-left py-2 px-3 border-b font-medium text-muted-foreground whitespace-nowrap">
                          Table
                        </th>
                        <th className="text-center py-2 px-2 border-b font-medium text-muted-foreground w-14">
                          RLS
                        </th>
                        {data.personas.map((persona) =>
                          data.operations.map((op) => (
                            <th
                              key={`${persona}-${op}`}
                              className="text-center py-2 px-1.5 border-b font-medium text-muted-foreground whitespace-nowrap"
                            >
                              <div className="text-[10px] leading-tight">{persona.split(" ")[0]}</div>
                              <div className="text-[10px] leading-tight opacity-70">{op}</div>
                            </th>
                          )),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data.tables.map((t) => {
                        const hasIssue = !t.rlsEnabled || (t.rlsEnabled && t.policyCount === 0);
                        return (
                          <tr
                            key={t.table}
                            className={cn(
                              "hover:bg-muted/30",
                              hasIssue && "bg-amber-500/5",
                            )}
                          >
                            <td className="py-1.5 px-3 border-b font-mono text-xs whitespace-nowrap">
                              <span className="flex items-center gap-1.5">
                                {t.table}
                                {!t.rlsEnabled && (
                                  <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[10px] px-1 py-0">
                                    no RLS
                                  </Badge>
                                )}
                                {t.rlsEnabled && t.policyCount === 0 && (
                                  <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px] px-1 py-0">
                                    0 policies
                                  </Badge>
                                )}
                              </span>
                            </td>
                            <td className="py-1.5 px-2 border-b text-center">
                              {t.rlsEnabled ? (
                                <span className="text-emerald-500 text-xs font-medium">ON</span>
                              ) : (
                                <span className="text-red-500 text-xs font-medium">OFF</span>
                              )}
                            </td>
                            {data.personas.map((persona) =>
                              data.operations.map((op) => {
                                const cell = t.matrix[persona]?.[op];
                                if (!cell) {
                                  return (
                                    <td key={`${persona}-${op}`} className="py-1.5 px-1.5 border-b text-center">
                                      <span className="text-muted-foreground/30">-</span>
                                    </td>
                                  );
                                }
                                return (
                                  <td
                                    key={`${persona}-${op}`}
                                    className="py-1.5 px-1.5 border-b text-center"
                                    title={cell.error || (cell.rowCount != null ? `${cell.rowCount} rows` : undefined)}
                                  >
                                    {cell.error ? (
                                      <XCircle className="h-3.5 w-3.5 text-red-500 mx-auto" />
                                    ) : cell.allowed ? (
                                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mx-auto" />
                                    ) : (
                                      <XCircle className="h-3.5 w-3.5 text-red-500 mx-auto" />
                                    )}
                                  </td>
                                );
                              }),
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Legend */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  Allowed
                </div>
                <div className="flex items-center gap-1">
                  <XCircle className="h-3 w-3 text-red-500" />
                  Denied / Error
                </div>
                <div className="flex items-center gap-1">
                  <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[10px] px-1 py-0">no RLS</Badge>
                  RLS disabled
                </div>
                <div className="flex items-center gap-1">
                  <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px] px-1 py-0">0 policies</Badge>
                  RLS on, no policies (all denied)
                </div>
              </div>

              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={fetchBatchTest}>
                  <RefreshCw className="h-3 w-3 mr-1.5" />
                  Re-run Tests
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
